/**
 * Hook per ottenere il dettaglio di un corso
 *
 * GET /corsi/{id}
 *
 * Usato in edit mode per popolare il form con i dati esistenti.
 */

import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/core/api/axios-client";
import type { CorsoDettaglio, CorsoDettaglioResponse } from "@/core/api/types/corso";

/**
 * Fetch dettaglio corso
 */
async function fetchCorso(id: string): Promise<CorsoDettaglio> {
  const response = await apiClient.get<CorsoDettaglioResponse>(`/corsi/${id}`);
  return response.data.data;
}

/**
 * Hook per ottenere il dettaglio di un corso
 *
 * @param id - ID del corso (id_corso)
 * @returns Query result con i dati del corso
 *
 * @example
 * ```tsx
 * const { data: corso, isLoading } = useCorso(id);
 *
 * if (isLoading) return <Loading />;
 *
 * // Popola form con corso.nome_corso, corso.codice, etc.
 * ```
 */
export function useCorso(id: string | undefined) {
  return useQuery({
    queryKey: ["corso", id],
    queryFn: () => fetchCorso(id!),
    enabled: !!id, // Non esegue se id è undefined/null
    staleTime: 5 * 60 * 1000, // Cache 5 minuti
  });
}

export default useCorso;
